import React, { useState, useEffect, useContext } from "react";

import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";

import { getMuiLocale } from "./translate/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import CssBaseline from "@material-ui/core/CssBaseline";
import ColorModeContext from "./layout/themeContext";
import { SocketContext, SocketManager } from './context/Socket/SocketContext';
import { ForwardMessageProvider } from "./context/ForwardingMessage/ForwardingMessageContext";
import { EditingMessageProvider } from "./context/EditingMessage/EditingMessageContext";
import { PendingMessagesProvider } from "./context/PendingMessages/PendingMessagesContext";
import { OptimisticMessagesProvider } from "./context/OptimisticMessages/OptimisticMessagesContext";

import { ForwardMessageContext } from "./context/ForwardingMessage/ForwardingMessageContext";
import ForwardMessageModal from "./components/ForwardMessageModal";
import FollowUpPromptDialog from "./components/FollowUpPromptDialog";
import configureToastPolicy from "./utils/toastPolicy";


import Routes from "./routes";

configureToastPolicy();

const queryClient = new QueryClient();

// Limite de itens visíveis nos dropdowns (Select e Autocomplete); acima disso, rola.
// MenuItem do MUI v4: ~36px no desktop, 48px abaixo de 600px; a lista tem 8px de
// padding em cima e embaixo.
const DROPDOWN_MAX_ITEMS = 8;
const dropdownMaxHeight = (itemHeight) => DROPDOWN_MAX_ITEMS * itemHeight + 16;

const dropdownListStyles = (mode) => ({
    maxHeight: dropdownMaxHeight(36),
    overflowY: "auto",
    "@media (max-width: 599.95px)": {
        maxHeight: dropdownMaxHeight(48),
    },
    "&::-webkit-scrollbar": {
        width: "8px",
    },
    "&::-webkit-scrollbar-thumb": {
        borderRadius: "4px",
        backgroundColor: mode === "light" ? "rgba(0, 0, 0, 0.25)" : "rgba(255, 255, 255, 0.3)",
    },
});

// Autor: GitHub Copilot | Data: 28/03/2026 | Motivo: isolar o consumo do contexto de encaminhamento
// para evitar re-renderização desnecessária de toda a árvore de rotas ao abrir/fechar o modal.
const ForwardMessageModalController = React.memo(() => {
    const {
        selectedForwardMessages,
        isForwardModalOpen,
        clearForwardSelection
    } = useContext(ForwardMessageContext);

    const handleCloseForwardModal = () => {
        clearForwardSelection();
    };

    return (
        <ForwardMessageModal
            open={isForwardModalOpen && selectedForwardMessages.length > 0}
            onClose={handleCloseForwardModal}
        />
    );
});

const AppContent = () => {

    return (
        <>
            <Routes />
            <ForwardMessageModalController />
            <FollowUpPromptDialog />
        </>
    );
};

const App = () => {

    const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
    const preferredTheme = window.localStorage.getItem("preferredTheme");
    const [mode, setMode] = useState(preferredTheme ? preferredTheme : prefersDarkMode ? "dark" : "light");

    const colorMode = React.useMemo(
        () => ({
            toggleColorMode: () => {
                setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
            },
        }),
        []
    );

    const theme = createTheme(
        {
            scrollbarStyles: {
                "&::-webkit-scrollbar": {
                    width: '8px',
                    height: '8px',
                },
                "&::-webkit-scrollbar-thumb": {
                    boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.3)',
                    backgroundColor: "#00aeef",
                },
            },
            scrollbarStylesSoft: {
                "&::-webkit-scrollbar": {
                    width: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                    backgroundColor: mode === "light" ? "#F3F3F3" : "#333333",
                },
            },
            palette: {
                type: mode,
                primary: { main: mode === "light" ? "#00aeef" : "#FFFFFF" },
                textPrimary: mode === "light" ? "#00aeef" : "#FFFFFF",
                borderPrimary: mode === "light" ? "#00aeef" : "#FFFFFF",
                dark: { main: mode === "light" ? "#333333" : "#F3F3F3" },
                light: { main: mode === "light" ? "#F3F3F3" : "#333333" },
                tabHeaderBackground: mode === "light" ? "#EEE" : "#666",
                optionsBackground: mode === "light" ? "#fafafa" : "#333",
				options: mode === "light" ? "#fafafa" : "#666",
				fontecor: mode === "light" ? "#128c7e" : "#fff",
                fancyBackground: mode === "light" ? "#fafafa" : "#333",
				bordabox: mode === "light" ? "#eee" : "#333",
				newmessagebox: mode === "light" ? "#eee" : "#333",
				inputdigita: mode === "light" ? "#fff" : "#666",
				contactdrawer: mode === "light" ? "#fff" : "#666",
				announcements: mode === "light" ? "#ededed" : "#333",
				login: mode === "light" ? "#fff" : "#1C1C1C",
				announcementspopover: mode === "light" ? "#fff" : "#666",
				chatlist: mode === "light" ? "#eee" : "#666",
				boxlist: mode === "light" ? "#ededed" : "#666",
				boxchatlist: mode === "light" ? "#ededed" : "#333",
                total: mode === "light" ? "#fff" : "#222",
                messageIcons: mode === "light" ? "grey" : "#F3F3F3",
                inputBackground: mode === "light" ? "#FFFFFF" : "#333",
                barraSuperior: mode === "light" ? "linear-gradient(to right, #00aeef, #00aeef , #00aeef)" : "#666",
				boxticket: mode === "light" ? "#EEE" : "#666",
				campaigntab: mode === "light" ? "#ededed" : "#666",
				mediainput: mode === "light" ? "#ededed" : "#1c1c1c",
            },
            overrides: {
                // role="listbox" só existe no menu de <Select>/TextField select:
                // menus de ação (contexto do ticket, opções da mensagem) não mudam.
                MuiMenu: {
                    list: {
                        '&[role="listbox"]': dropdownListStyles(mode),
                    },
                },
                MuiAutocomplete: {
                    listbox: dropdownListStyles(mode),
                },
            },
            mode,
        },
        getMuiLocale()
    );

    useEffect(() => {
        window.localStorage.setItem("preferredTheme", mode);
    }, [mode]);

    return (
        <ColorModeContext.Provider value={{ colorMode }}>
            <ThemeProvider theme={theme}>
                {/* Fora do ThemeProvider o body ficava sempre claro (#fafafa e texto escuro) no modo escuro */}
                <CssBaseline />
                <QueryClientProvider client={queryClient}>
                  <SocketContext.Provider value={SocketManager}>
                    <ForwardMessageProvider>
                      <EditingMessageProvider>
                        <PendingMessagesProvider>
                          <OptimisticMessagesProvider>
                            <AppContent />
                          </OptimisticMessagesProvider>
                        </PendingMessagesProvider>
                      </EditingMessageProvider>
                    </ForwardMessageProvider>
                  </SocketContext.Provider>
                </QueryClientProvider>
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
};

export default App;
